<template>
  <div>
    <div>
      <div class="col-12 d-flex flex-wrap filter-row">
        <el-select
          class="select-primary pagination-select"
          v-model="pagination.perPage"
          :placeholder="$t('COMMON.PER_PAGE')"
        >
          <el-option
            class="select-primary"
            v-for="item in pagination.perPageOptions"
            :key="item"
            :label="item"
            :value="item"
          >
          </el-option>
        </el-select>

        <organization-selector
          v-if="
            !filterOrganization &&
            !filterRecipient &&
            $currentUserCan($permissions.PERM_VIEW_ANY_ORGANIZATIONS)
          "
          @organizationChanged="
            (organizationId) => (selectedOrganization = organizationId)
          "
        />

        <base-input
          v-if="
            !filterEstablishment &&
            $currentUserCan($permissions.PERM_VIEW_ANY_ESTABLISHMENTS)
          "
        >
          <establishment-selector
            :establishment="selectedEstablishment"
            :filterOrganization="selectedOrganization || filterOrganization"
            :showAll="true"
            @establishmentChanged="
              (establishmentId) => (selectedEstablishment = establishmentId)
            "
          />
        </base-input>

        <base-input v-if="!filterRecipient">
          <customer-selector
            :customer="selectedCustomerId"
            :filterOrganization="selectedOrganization || filterOrganization"
            :showAll="true"
            :allOptionLabel="$t('COMMON.ALL_RECIPIENTS')"
            @customerChanged="(customerId) => (selectedCustomerId = customerId)"
          />
        </base-input>
      </div>
      <div class="col-12 d-flex flex-wrap filter-row">
        <el-select
          v-if="!filterStatus"
          :placeholder="$t('COMMON.STATUS')"
          v-model="selectedStatus"
        >
          <el-option :value="0" :label="$t('COMMON.ALL_STATUSES')"> </el-option>
          <el-option
            v-for="status in statusesOptions"
            :key="status"
            :value="status"
            :label="$t(`PURCHASES_ORDERS.ORDER_STATUS_${status}`)"
          >
          </el-option>
        </el-select>

        <el-select
          v-if="!filterStatus"
          :placeholder="$t('SALES_ORDERS.INVOICING_STATUS')"
          v-model="selectedInvoicingStatus"
        >
          <el-option :value="0" :label="$t('COMMON.ALL_STATUSES')"> </el-option>
          <el-option
            v-for="status in invoicingStatusesOptions"
            :key="status"
            :value="status"
            :label="$t(`SALES_ORDERS.INVOICING_STATUS_${status}`)"
          >
          </el-option>
        </el-select>

        <el-select
          v-if="
            $currentUserCan($permissions.PERM_VIEW_ANY_SALES_DELIVERIES) &&
            !filterStatus
          "
          :placeholder="$t('SALES_ORDERS.DELIVERY_STATUS')"
          v-model="selectedDeliveryStatus"
        >
          <el-option :value="0" :label="$t('COMMON.ALL_STATUSES')"> </el-option>
          <el-option
            v-for="status in deliveryStatusesOptions"
            :key="status"
            :value="status"
            :label="$t(`SALES_ORDERS.DELIVERY_STATUS_${status}`)"
          >
          </el-option>
        </el-select>

        <el-select
          v-if="!filterStatus"
          :placeholder="$t('SALES_ORDERS.CLIENT_VALIDATION')"
          v-model="selectedClientValidatedStatus"
        >
          <el-option :value="0" :label="$t('COMMON.ALL_STATUSES')"> </el-option>
          <el-option
            :key="CLIENT_VALIDATED_STATUS_PENDING"
            :value="CLIENT_VALIDATED_STATUS_PENDING"
            :label="$t(`SALES_ORDERS.CLIENT_VALIDATED_STATUS_PENDING`)"
          >
          </el-option>
          <el-option
            :key="CLIENT_VALIDATED_STATUS_VALIDATED"
            :value="CLIENT_VALIDATED_STATUS_VALIDATED"
            :label="$t(`SALES_ORDERS.CLIENT_VALIDATED_STATUS_VALIDATED`)"
          >
          </el-option>
          <el-option
            :key="CLIENT_VALIDATED_STATUS_CANCELED"
            :value="CLIENT_VALIDATED_STATUS_CANCELED"
            :label="$t(`SALES_ORDERS.CLIENT_VALIDATED_STATUS_CANCELED`)"
          >
          </el-option>
        </el-select>

        <el-select
          v-if="!filterStatus"
          :placeholder="$t('SALES_ORDERS.PRICING_TYPE')"
          v-model="selectedPricingType"
        >
          <el-option :value="0" :label="$t('COMMON.ALL_TYPES')"> </el-option>
          <el-option
            v-for="pricingType in pricingTypeOptions"
            :key="pricingType"
            :value="pricingType"
            :label="
              $t(`SALES_ORDERS.PRICING_TYPE_${pricingType.toUpperCase()}`)
            "
          >
          </el-option>
        </el-select>

        <base-input class="dates" :placeholder="$t('COMMON.DATES_PLACEHOLDER')">
          <flat-picker
            :config="{
              allowInput: true,
              mode: 'range',
              locale: $flatPickrLocale(),
            }"
            class="form-control datepicker"
            v-model="dateRange"
            :placeholder="$t('COMMON.DATES_PLACEHOLDER')"
          >
          </flat-picker>
        </base-input>

        <div class="code">
          <base-input
            v-model="query"
            type="search"
            prepend-icon="fas fa-search"
            :placeholder="$t('COMMON.SEARCH_HINT.SALES_ORDER')"
            clearable
          />
        </div>
      </div>
      <el-table
        class="align-items-center table-flush"
        header-row-class-name="thead-light"
        :data="salesOrders"
        @sort-change="sortChange"
      >
        <div slot="empty" v-if="loading">
          <img src="/img/loading.gif" style="height: 100px; width: 100px" />
        </div>
        <el-table-column
          :label="$t('COMMON.NUMBER')"
          min-width="120px"
          sortable="custom"
          prop="seq_number"
        >
          <template slot-scope="{ row }">
            {{ row.seq_number ?? row.code }}
          </template>
        </el-table-column>
        <el-table-column
          :label="$t('COMMON.STATUS')"
          prop="status"
          min-width="300px"
        >
          <template slot-scope="{ row }">
            <sales-order-status-badge :salesOrder="row" />
          </template>
        </el-table-column>
        <el-table-column
          :label="$t('SALES_ORDERS.CLIENT_VALIDATION')"
          prop="status"
          min-width="180px"
        >
          <template slot-scope="{ row }">
            <sales-order-client-validated-status-badge :salesOrder="row" />
          </template>
        </el-table-column>
        <el-table-column
          :label="$t('COMMON.TYPE')"
          prop="pricing_type"
          min-width="180px"
        >
          <template slot-scope="{ row }">
            {{
              $t(
                `SALES_ORDERS.PRICING_TYPE_${(
                  row.pricing_type || PRICING_TYPE_STANDARD
                ).toUpperCase()}`
              )
            }}
          </template>
        </el-table-column>
        <el-table-column
          :label="$t('COMMON.AMOUNT')"
          prop="pricing.total"
          min-width="200px"
        >
          <template v-slot="{ row }">
            <span>
              {{ $formatCurrency(row.pricing.total) }}
            </span>
          </template>
        </el-table-column>
        <el-table-column
          :label="$t('COMMON.ESTABLISHMENT')"
          v-if="
            !filterEstablishment &&
            $currentUserCan($permissions.PERM_VIEW_ANY_ESTABLISHMENTS)
          "
          min-width="250px"
        >
          <template v-slot="{ row }">
            <object-link :object="row.establishment" />
          </template>
        </el-table-column>
        <el-table-column :label="$t('COMMON.RECIPIENT')" min-width="250px">
          <template v-slot="{ row }">
            <object-link :object="row.recipient" />
          </template>
        </el-table-column>
        <el-table-column
          :label="$t('SALES_ORDERS.EVENT_NAME')"
          min-width="250px"
        >
          <template v-slot="{ row }">
            {{ row.event_name }}
          </template>
        </el-table-column>
        <el-table-column
          :label="$t('COMMON.ORGANIZATION')"
          v-if="
            !filterOrganization &&
            $currentUserCan($permissions.PERM_VIEW_ANY_ORGANIZATIONS)
          "
          min-width="250px"
        >
          <template v-slot="{ row }">
            <organization :organization="row.organization" />
          </template>
        </el-table-column>
        <!-- <el-table-column
          :label="$t('SALES_ORDERS.EXPIRATION_TIME')"
          prop="expiration_time"
          sortable="custom"
        /> -->
        <el-table-column
          :label="$t('SALES_ORDERS.EVENT_DATE')"
          prop="event_date"
          sortable="custom"
          min-width="200px"
        >
          <template v-slot="{ row }">
            {{ row.event_date ? $timeZoneDateFormat(row.event_date) : "" }}
          </template>
        </el-table-column>
        <el-table-column min-width="150px" align="center" fixed="right">
          <div slot-scope="{ row }" class="table-actions">
            <el-tooltip
              :content="$t('COMMON.VIEW')"
              placement="top"
              v-if="$currentUserCan($permissions.PERM_VIEW_SALES_ORDERS)"
            >
              <a
                type="text"
                @click="viewSalesOrder(row)"
                class="table-action"
                data-toggle="tooltip"
                style="cursor: pointer"
              >
                <i class="fas fa-eye"></i>
              </a>
            </el-tooltip>

            <el-tooltip
              :content="$t('COMMON.EDIT')"
              placement="top"
              v-if="
                $currentUserCan($permissions.PERM_EDIT_SALES_ORDERS) &&
                row.status == ORDER_STATUS_DRAFT &&
                !hideUpdateAndDelete
              "
            >
              <a
                type="text"
                @click="editSalesOrder(row)"
                class="table-action"
                data-toggle="tooltip"
                style="cursor: pointer"
              >
                <i class="fas fa-edit"></i>
              </a>
            </el-tooltip>

            <el-tooltip
              :content="$t('COMMON.DELETE')"
              placement="top"
              v-if="
                $currentUserCan($permissions.PERM_DELETE_SALES_ORDERS) &&
                row.status == ORDER_STATUS_DRAFT &&
                !hideUpdateAndDelete
              "
            >
              <a
                type="text"
                @click="deleteSalesOrder(row.id)"
                class="table-action table-action-delete"
                data-toggle="tooltip"
                style="cursor: pointer"
              >
                <i class="fas fa-trash"></i>
              </a>
            </el-tooltip>
          </div>
        </el-table-column>
      </el-table>
    </div>
    <div
      slot="footer"
      class="mt-5 col-12 d-flex justify-content-center justify-content-sm-between flex-wrap table-footer"
    >
      <div class="">
        <p class="card-category">
          {{
            $t("COMMON.DISPLAY_FROM_X_TO_X_OF_X_ENTRIES", {
              from: total ? from + 1 : 0,
              to: to,
              of: total,
            })
          }}

          <span v-if="selectedRows.length">
            &nbsp; &nbsp;
            {{ $t("COMMON.X_LINES_SELECTED", { count: selectedRows.length }) }}
          </span>
        </p>
      </div>
      <base-pagination
        class="pagination-no-border"
        v-model="pagination.currentPage"
        :per-page="pagination.perPage"
        :total="total"
      />
    </div>
  </div>
</template>
<script>
import _ from "lodash";
import {
  Table,
  TableColumn,
  DropdownMenu,
  DropdownItem,
  Dropdown,
  Tooltip,
  Select,
  Option,
  Button,
} from "element-ui";
import swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.css";
import flatPicker from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import requestErrorMixin from "@/mixins/request-error-mixin";
import {
  orderStatusesOption,
  orderInvoicingStatusesOption,
  orderDeliveryStatusesOption,
  ORDER_STATUS_DRAFT,
} from "@/constants/orders";
import {
  CLIENT_VALIDATED_STATUS_DRAFT,
  CLIENT_VALIDATED_STATUS_PENDING,
  CLIENT_VALIDATED_STATUS_CANCELED,
  CLIENT_VALIDATED_STATUS_VALIDATED,
} from "@/constants/salesOrders";
import {
  PRICING_TYPE_STANDARD,
  PRICING_TYPE_SUBSIDIZED,
  PRICING_TYPE_CATERER,
} from "@/constants/pricingTypes";
import { BasePagination } from "@/components";
import OrganizationSelector from "@/components/OrganizationSelector.vue";
import EstablishmentSelector from "@/components/EstablishmentSelector.vue";
import CustomerSelector from "@/components/CustomerSelector.vue";
import SalesOrderStatusBadge from "./SalesOrderStatusBadge.vue";
import SalesOrderClientValidatedStatusBadge from "./SalesOrderClientValidatedStatusBadge.vue";

export default {
  name: "sales-order-list-table",

  components: {
    BasePagination,
    flatPicker,
    OrganizationSelector,
    EstablishmentSelector,
    CustomerSelector,
    SalesOrderStatusBadge,
    SalesOrderClientValidatedStatusBadge,
    [Tooltip.name]: Tooltip,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Dropdown.name]: Dropdown,
    [DropdownItem.name]: DropdownItem,
    [DropdownMenu.name]: DropdownMenu,
    [Select.name]: Select,
    [Option.name]: Option,
    [Button.name]: Button,
  },

  mixins: [requestErrorMixin],

  props: {
    filterOrganization: {
      type: String,
      default: null,
      description: "Organization id",
    },
    filterRecipient: {
      type: Object,
      default: null,
      description: "Object with 'recipient_id' and 'recipient_type' ",
    },
    filterStatus: {
      type: String,
      default: null,
      description: "Status",
    },
    filterServiceCenter: {
      type: String,
      default: null,
      description: "Service center id",
    },
    filterEstablishment: {
      type: String,
      default: null,
      description: "Establishment id",
    },
    filterCafeteria: {
      type: String,
      default: null,
      description: "Cafeteria id",
    },
    hideUpdateAndDelete: {
      type: Boolean,
      default: false,
      description: "Hide update and delete actions",
    },
  },

  data() {
    return {
      query: null,
      selectedRows: [],
      sort: "-created_at",
      total: 0,
      pagination: {
        perPage: 20,
        currentPage: 1,
        perPageOptions: [20, 50, 100, 500],
      },
      salesOrders: [],
      loading: true,
      dateRange: null,
      selectedOrganization: null,
      selectedEstablishment: null,
      selectedCustomerId: null,
      selectedStatus: null,
      selectedInvoicingStatus: null,
      selectedDeliveryStatus: null,
      selectedRecipient: null,
      selectedClientValidatedStatus: null,
      selectedPricingType: null,
      statusesOptions: orderStatusesOption,
      invoicingStatusesOptions: orderInvoicingStatusesOption,
      deliveryStatusesOptions: orderDeliveryStatusesOption,
      pricingTypeOptions: [
        PRICING_TYPE_STANDARD,
        PRICING_TYPE_SUBSIDIZED,
        PRICING_TYPE_CATERER,
      ],
      ORDER_STATUS_DRAFT: ORDER_STATUS_DRAFT,
      PRICING_TYPE_STANDARD,
      CLIENT_VALIDATED_STATUS_DRAFT,
      CLIENT_VALIDATED_STATUS_PENDING,
      CLIENT_VALIDATED_STATUS_CANCELED,
      CLIENT_VALIDATED_STATUS_VALIDATED,
    };
  },

  computed: {
    from() {
      return this.pagination.perPage * (this.pagination.currentPage - 1);
    },

    to() {
      let highBound = this.from + this.pagination.perPage;
      if (this.total < highBound) {
        highBound = this.total;
      }
      return highBound;
    },
  },

  watch: {
    query: {
      handler: "getListDebounced",
      immediate: true,
    },
    pagination: {
      handler: "getList",
      immediate: false,
      deep: true,
    },
    filterOrganization: {
      handler: "getListDebounced",
      immediate: true,
    },
    selectedOrganization: {
      handler: "getListDebounced",
      immediate: true,
    },
    selectedEstablishment: {
      handler: "getListDebounced",
      immediate: true,
    },
    selectedCustomerId: {
      handler: "getListDebounced",
      immediate: true,
    },
    filterRecipient: {
      handler: "getListDebounced",
      immediate: true,
    },
    selectedRecipient: {
      handler: "getListDebounced",
      immediate: true,
    },
    filterStatus: {
      handler: "getListDebounced",
      immediate: true,
    },
    selectedStatus: {
      handler: "getListDebounced",
      immediate: true,
    },
    selectedInvoicingStatus: {
      handler: "getListDebounced",
      immediate: true,
    },
    selectedDeliveryStatus: {
      handler: "getListDebounced",
      immediate: true,
    },
    selectedClientValidatedStatus: {
      handler: "getListDebounced",
      immediate: true,
    },
    selectedPricingType: {
      handler: "getListDebounced",
      immediate: true,
    },
    dateRange: {
      handler: "getListDebounced",
      immediate: false,
      deep: true,
    },
    filterServiceCenter: {
      handler: "getListDebounced",
      immediate: true,
    },
    filterEstablishment: {
      handler: "getListDebounced",
      immediate: true,
    },
    filterCafeteria: {
      handler: "getListDebounced",
      immediate: true,
    },
  },

  methods: {
    getListDebounced: _.debounce(function () {
      this.getList();
    }, 300),

    async getList() {
      try {
        const vDateRange = this.dateRange
          ? this.dateRange.split(this.$flatPickrLocale().rangeSeparator)
          : [];
        let params = {
          ...(this.sort ? { sort: this.sort } : {}),
          filter: {
            ...(this.query ? { search: encodeURIComponent(this.query) } : {}),
            ...(vDateRange.length == 2
              ? {
                  created_at: [
                    vDateRange[0] + " 00:00:00",
                    vDateRange[1] + " 23:59:59",
                  ],
                }
              : {}),
          },
          page: {
            number: this.pagination.currentPage,
            size: this.pagination.perPage,
          },
          include: "organization,establishment,recipient",
        };

        if (this.filterOrganization) {
          params = {
            ...params,
            filter: { ...params.filter, organization: this.filterOrganization },
          };
        }
        if (this.selectedOrganization) {
          params = {
            ...params,
            filter: {
              ...params.filter,
              organization: this.selectedOrganization,
            },
          };
        }
        if (this.filterRecipient) {
          if (this.filterRecipient.type && this.filterRecipient.id) {
            params = {
              ...params,
              filter: {
                ...params.filter,
                recipient_type: this.filterRecipient.type,
                recipient_id: this.filterRecipient.id,
              },
            };
          }
        }
        if (this.selectedRecipient) {
          if (this.selectedRecipient.type && this.selectedRecipient.id) {
            params = {
              ...params,
              filter: {
                ...params.filter,
                recipient_type: this.selectedRecipient.type,
                recipient_id: this.selectedRecipient.id,
              },
            };
          }
        }
        if (this.filterStatus) {
          params = {
            ...params,
            filter: { ...params.filter, status: this.filterStatus },
          };
        }
        if (this.selectedStatus) {
          params = {
            ...params,
            filter: {
              ...params.filter,
              status: this.selectedStatus,
            },
          };
        }
        if (this.selectedInvoicingStatus) {
          params = {
            ...params,
            filter: {
              ...params.filter,
              invoicing_status: this.selectedInvoicingStatus,
            },
          };
        }
        if (this.selectedDeliveryStatus) {
          params = {
            ...params,
            filter: {
              ...params.filter,
              delivery_status: this.selectedDeliveryStatus,
            },
          };
        }
        if (this.selectedClientValidatedStatus) {
          params = {
            ...params,
            filter: {
              ...params.filter,
              client_validated_status: [this.selectedClientValidatedStatus],
            },
          };
        }
        if (this.selectedPricingType) {
          params = {
            ...params,
            filter: {
              ...params.filter,
              pricing_type: this.selectedPricingType,
            },
          };
        }
        if (this.selectedEstablishment) {
          params = {
            ...params,
            filter: {
              ...params.filter,
              establishment: this.selectedEstablishment,
            },
          };
        }
        if (this.selectedCustomerId) {
          params = {
            ...params,
            filter: {
              ...params.filter,
              recipient_type: "customers",
              recipient_id: this.selectedCustomerId,
            },
          };
        }
        if (this.filterServiceCenter) {
          params = {
            ...params,
            filter: {
              ...params.filter,
              serviceCenter: this.filterServiceCenter,
            },
          };
        }
        if (this.filterEstablishment) {
          params = {
            ...params,
            filter: {
              ...params.filter,
              establishment: this.filterEstablishment,
            },
          };
        }
        if (this.filterCafeteria) {
          params = {
            ...params,
            filter: { ...params.filter, cafeteria: this.filterCafeteria },
          };
        }

        await this.$store.dispatch("salesOrders/list", params);
        this.salesOrders = this.$store.getters["salesOrders/list"];
        this.total = this.$store.getters["salesOrders/listTotal"];
        this.loading = false;
      } catch (error) {
        this.$notify({
          type: "danger",
          message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
        });
      }
    },

    async deleteSalesOrder(id) {
      const confirmation = await swal.fire({
        title: this.$t("SALES_ORDERS.DELETE_THIS_SALES_ORDER"),
        type: "question",
        buttonsStyling: false,
        showCancelButton: true,
        confirmButtonText: this.$t("COMMON.YES"),
        cancelButtonText: this.$t("COMMON.NO"),
        confirmButtonClass: "btn btn-primary",
        cancelButtonClass: "btn btn-warning",
      });

      try {
        if (confirmation.value === true) {
          await this.$store.dispatch("salesOrders/destroy", id);
          await this.getList();
          this.$notify({
            type: "success",
            message: this.$t("SALES_ORDERS.SALES_ORDER_DELETED"),
          });
        }
      } catch (error) {
        await this.showRequestError(error);
        this.$notify({
          type: "danger",
          message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
        });
      }
    },

    viewSalesOrder(salesOrder) {
      this.$router.push({
        name: "View SalesOrder",
        params: { id: salesOrder.id },
      });
    },

    async editSalesOrder(row) {
      this.$router.push({
        name: "Edit SalesOrder",
        params: { id: row.id },
      });
    },

    sortChange({ prop, order }) {
      if (order === "descending") {
        this.sort = `-${prop}`;
      } else {
        this.sort = `${prop}`;
      }
      this.getList();
    },
  },
};
</script>
